import axios from "axios";

// Your provided default base URL (can be overridden via env var)
// const BASE_URL = "https://www.mlveda.com/ShopifyApps/FeatureSuggestTest";
const BASE_URL = "https://www.mlveda.com/ShopifyApps/FeatureSuggest";

class FeedbackApiService {
  constructor() {
    this.jwtToken = null;
    this.tokenInitialized = null;
    this.userEmail = null;
    this.userName = null;
    this.appName = null;

    this.axiosInstance = axios.create({
      baseURL: BASE_URL,
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
    });

    this.axiosInstance.interceptors.request.use((config) => {
      config.headers = config.headers || {};

      if (this.jwtToken && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${this.jwtToken}`;
      }

      // Let browser set correct Content-Type for FormData
      if (config.data instanceof FormData) {
        delete config.headers["Content-Type"];
      } else if (!config.headers["Content-Type"]) {
        config.headers["Content-Type"] = "application/json";
      }

      return config;
    });
  }

  async fetchToken(appName) {
    try {
      const response = await axios.post(
        `${BASE_URL}/api/users/token`,
        { appName },
        {
          headers: { "Content-Type": "application/json" },
          maxBodyLength: Infinity,
        }
      );

      const token = response?.data?.data?.token;
      if (!token) throw new Error("Token not found in API response");

      this.jwtToken = token;
      this.axiosInstance.defaults.headers.common.Authorization = `Bearer ${token}`;
      return token;
    } catch (error) {
      const reason =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch JWT token";
      throw new Error(reason);
    }
  }

  async initializeToken(appName, userName, userEmail) {
    if (this.tokenInitialized) return this.tokenInitialized;

    this.appName = appName;
    if (userName) this.userName = userName;
    if (userEmail) this.userEmail = userEmail;

    this.tokenInitialized = this.fetchToken(appName).then(() => undefined);
    return this.tokenInitialized;
  }

  getToken() {
    if (!this.jwtToken) {
      throw new Error(
        "JWT token not initialized. Call initializeToken() first."
      );
    }
    return this.jwtToken;
  }

  async createFeatureRequest(data) {
    const formData = new FormData();
    formData.append("userEmail", data.userEmail);
    formData.append("userName", data.userName);
    formData.append("platform", data.platform || "Shopify");
    formData.append("title", data.title);
    formData.append("description", data.description);
    formData.append("appName", data.appName);
    if (data.storeName) formData.append("storeName", data.storeName);

    if (data.image) {
      formData.append("image", data.image);
    }

    // ensure token exists (initializeToken should have been called, but be defensive)
    const token = await this.fetchToken(data.appName);
    const response = await this.axiosInstance.post("/api/features", formData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  }

  async getUserId(email) {
    const response = await this.axiosInstance.get(`/api/users/${email}`);
    return response.data;
  }

  async getFeatures(userId) {
    const params = {};
    if (userId) params.userId = userId;
    params.limit = 500;
    const response = await this.axiosInstance.get("/api/features", { params });
    return response.data;
  }

  async updateFeature(featureId, data) {
    const formData = new FormData();
    formData.append("userId", data.userId);
    formData.append("title", data.title);
    if (data.description) formData.append("description", data.description);
    if (data.image) formData.append("image", data.image);
    // Support explicit image removal (backend expects empty string)
    if (data.image === "") formData.append("image", "");
    // if (data.storeName) formData.append("storeName", data.storeName);

    const token = await this.fetchToken(this.appName || data.appName || "cart-pencil");
    const response = await this.axiosInstance.put(
      `/api/features/${featureId}`,
      formData,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  }

  async voteOnFeature(featureId, action, userName, userEmail) {
    if (!this.userEmail || !this.userName || !this.appName) {
      throw new Error(
        "User context not initialized. Call initializeToken() first."
      );
    }

    const payload = {
      action,
      userEmail: userEmail || this.userEmail,
      userName: userName || this.userName,
      appName: this.appName,
      platform: "shopify",
    };

    const response = await this.axiosInstance.post(
      `/api/features/${featureId}/vote`,
      payload
    );
    return response.data;
  }
}

export default new FeedbackApiService();

